import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Match } from '@shared/types';

const MATCH_QUERY_KEY = (matchId: string) => ['match', matchId];

/**
 * Hook to fetch and subscribe to match updates
 * Uses polling with 2-second interval for real-time updates
 */
export function useMatch(matchId: string | undefined) {
  return useQuery<Match>({
    queryKey: MATCH_QUERY_KEY(matchId || ''),
    queryFn: () => api.getMatch(matchId!),
    enabled: !!matchId,
    refetchInterval: 2000, // Poll every 2 seconds for real-time updates
    refetchIntervalInBackground: true,
  });
}

/**
 * Hook to handle reset confirmation
 */
export function useConfirmReset(matchId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (confirmed: boolean) => api.confirmReset(matchId, confirmed),
    onSuccess: (data) => {
      // Update the match cache with the new data
      queryClient.setQueryData(MATCH_QUERY_KEY(matchId), data.match);
    },
  });
}

/**
 * Hook to check if there's a pending reset request for a match
 */
export function usePendingReset(match: Match | undefined) {
  if (!match) return false;
  
  // For now, we'll rely on the backend to handle reset requests
  // and the UI will show the modal based on backend state
  return false;
}
